import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sliders, ChevronLeft, ChevronRight, RotateCcw, PauseCircle, Trophy, ImageIcon, Loader2, ArrowLeft, Zap, ZapOff, Monitor } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import ImagecompoNav from '@/components/ImagecompoNav'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { imagecompoAPI } from '../lib/api'
import PageHeader from '../components/PageHeader'
import { useImagecompoSocket } from '../lib/imagecompoSocket'
import {
  openMIDI, listInputs, listOutputs, attachInputListener, eventKey,
  loadMappings, loadInputId, loadOutputId, normalizeMapping,
  applyLeds, clearAllLeds, PHASE_ORDER,
} from '../lib/imagecompoMidi'

const BASE_VIEWS = [
  { key: 'idle',    label: 'Idle',    icon: ImageIcon },
  { key: 'entry',   label: 'Entry',   icon: ImageIcon },
  { key: 'pauze',   label: 'Pauze',   icon: PauseCircle },
  { key: 'reveal',  label: 'Admin reveal', icon: Trophy },
]
const PUBLIC_VIEW = { key: 'public_reveal', label: 'Publiek reveal', icon: Trophy }
const RESULTS_VIEW = { key: 'results', label: 'Results', icon: Trophy }

export default function ImageCompoControlPage() {
  const [data, setData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [midiOn, setMidiOn] = useState(false)
  const [midiError, setMidiError] = useState('')
  const detachRef = useRef(null)
  const outputRef = useRef(null)
  const mappingsRef = useRef({})
  const dataRef = useRef(null)
  useEffect(() => { dataRef.current = data }, [data])

  // LEDs opnieuw uitzenden bij state/show veranderingen
  useEffect(() => {
    if (!midiOn || !outputRef.current || !data) return
    applyLeds(outputRef.current, mappingsRef.current, { state: data.state, show: data.show, entries: data.entries })
  }, [
    midiOn,
    data?.state?.current_view,
    data?.state?.current_entry_id,
    data?.state?.reveal_step,
    data?.state?.public_reveal_step,
    data?.show?.phase,
    data?.show?.submissions_open,
  ])

  const load = () => imagecompoAPI.getState().then(r => setData(r.data)).catch(() => {})
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const patch = async (p) => {
    setSaving(true)
    try { await imagecompoAPI.updateState(p); await load() }
    finally { setSaving(false) }
  }

  // MIDI action dispatcher — ondersteunt alle nieuwe acties + LED-eval context
  const runAction = async (actionKey /*, value */) => {
    const st = dataRef.current
    if (!st) return
    const cur = st.state || {}
    const show = st.show
    const entries = (st.entries || []).filter(e => e.status === 'approved')
    const step  = cur.reveal_step || 0
    const total = (st.reveal_order || []).length || entries.length
    const pstep  = cur.public_reveal_step || 0
    const ptotal = (st.public_reveal_order || []).length || entries.length
    const idx = entries.findIndex(e => e.id === cur.current_entry_id)

    // View
    if (actionKey.startsWith('view.'))         return imagecompoAPI.updateState({ current_view: actionKey.slice(5) })
    if (actionKey === 'emergency.blackout')    return imagecompoAPI.updateState({ current_view: 'idle' })
    // Entry navigatie
    if (actionKey === 'entry.next') return idx < entries.length - 1 && imagecompoAPI.updateState({ current_entry_id: entries[idx + 1].id })
    if (actionKey === 'entry.prev') return idx > 0 && imagecompoAPI.updateState({ current_entry_id: entries[idx - 1].id })
    if (actionKey.startsWith('entry.goto.')) {
      const n = Number(actionKey.slice(11))
      return entries[n - 1] && imagecompoAPI.updateState({ current_entry_id: entries[n - 1].id })
    }
    // Admin reveal
    if (actionKey === 'reveal.next')  return imagecompoAPI.updateState({ reveal_step: Math.min(step + 1, total) })
    if (actionKey === 'reveal.prev')  return imagecompoAPI.updateState({ reveal_step: Math.max(step - 1, 0) })
    if (actionKey === 'reveal.reset') return imagecompoAPI.updateState({ reveal_step: 0 })
    if (actionKey.startsWith('reveal.step.'))
      return imagecompoAPI.updateState({ reveal_step: Math.max(0, Math.min(total, Number(actionKey.slice(12)))) })
    // Public reveal
    if (actionKey === 'public_reveal.next')  return imagecompoAPI.updateState({ public_reveal_step: Math.min(pstep + 1, ptotal) })
    if (actionKey === 'public_reveal.prev')  return imagecompoAPI.updateState({ public_reveal_step: Math.max(pstep - 1, 0) })
    if (actionKey === 'public_reveal.reset') return imagecompoAPI.updateState({ public_reveal_step: 0 })
    if (actionKey.startsWith('public_reveal.step.'))
      return imagecompoAPI.updateState({ public_reveal_step: Math.max(0, Math.min(ptotal, Number(actionKey.slice(19)))) })
    // Phase
    if (actionKey === 'phase.next' || actionKey === 'phase.prev') {
      const i = PHASE_ORDER.indexOf(show?.phase)
      const nx = actionKey === 'phase.next' ? Math.min(PHASE_ORDER.length - 1, i + 1) : Math.max(0, i - 1)
      return imagecompoAPI.updateShow(show.id, { phase: PHASE_ORDER[nx] })
    }
    if (actionKey.startsWith('phase.'))
      return imagecompoAPI.updateShow(show.id, { phase: actionKey.slice(6) })
    // Show toggles
    if (actionKey === 'submissions.toggle')
      return imagecompoAPI.updateShow(show.id, { submissions_open: !show?.submissions_open })
  }

  const toggleMidi = async () => {
    if (midiOn) {
      if (detachRef.current) { detachRef.current(); detachRef.current = null }
      if (outputRef.current) clearAllLeds(outputRef.current)
      outputRef.current = null
      setMidiOn(false); return
    }
    try {
      setMidiError('')
      const access = await openMIDI()
      const inputs = listInputs(access)
      const outputs = listOutputs(access)
      const input  = inputs.find(i => i.id === loadInputId())   || inputs[0]
      const output = outputs.find(o => o.id === loadOutputId()) || outputs[0] || null
      if (!input) { setMidiError('Geen MIDI input gevonden'); return }
      const mappings = loadMappings()
      mappingsRef.current = mappings
      outputRef.current = output
      detachRef.current = attachInputListener(input, (evt) => {
        const key = eventKey(evt); if (!key) return
        const spec = normalizeMapping(mappings[key])
        if (!spec.action) return
        runAction(spec.action).then(load).catch(() => {})
      })
      // Initial LED sync
      if (output && dataRef.current) {
        applyLeds(output, mappings, { state: dataRef.current.state, show: dataRef.current.show, entries: dataRef.current.entries })
      }
      setMidiOn(true)
    } catch (e) { setMidiError(e.message) }
  }
  useEffect(() => () => {
    if (detachRef.current) detachRef.current()
    if (outputRef.current) clearAllLeds(outputRef.current)
  }, [])

  if (!data?.show) return (
    <div className="max-w-4xl mx-auto">
      <ImagecompoNav />
      <PageHeader icon={Sliders}  title="Image Compo · Control" />
      <Card><CardContent className="p-6 text-center text-muted-foreground">Geen actieve show.</CardContent></Card>
    </div>
  )

  const state = data.state || {}
  const entries = data.entries || []
  const approved = entries.filter(e => e.status === 'approved')
  const revealTotal       = (data.reveal_order || []).length || approved.length
  const publicRevealTotal = (data.public_reveal_order || []).length || approved.length
  const publicEnabled = !!data.show?.public_scoring_enabled
  const VIEWS = [
    ...BASE_VIEWS,
    ...(publicEnabled ? [PUBLIC_VIEW] : []),
    RESULTS_VIEW,
  ]

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <ImagecompoNav />
      <PageHeader icon={Sliders}  title="Control" subtitle={data.show.name}
        actions={
          <>
            <Button variant={midiOn ? 'default' : 'outline'} onClick={toggleMidi}>
              {midiOn ? <Zap className="h-4 w-4 mr-2 text-yellow-400" /> : <ZapOff className="h-4 w-4 mr-2" />}
              MIDI {midiOn ? 'aan' : 'uit'}
            </Button>
            <Button asChild variant="outline">
              <a href="/imagecompo/stage" target="_blank" rel="noopener"><Monitor className="h-4 w-4 mr-2" />Stage</a>
            </Button>
            <Button asChild variant="outline"><Link to="/imagecompo"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>
          </>
        }
      />
      {midiError && <div className="text-sm text-red-500">⚠ {midiError}</div>}

      {/* Fase view switcher */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Beamer view</CardTitle></CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          {VIEWS.map(v => {
            const Icon = v.icon
            const active = state.current_view === v.key
            return (
              <Button key={v.key} size="lg" variant={active ? 'default' : 'outline'} disabled={saving}
                      onClick={() => patch({ current_view: v.key })}>
                <Icon className="h-4 w-4 mr-2" />{v.label}
              </Button>
            )
          })}
        </CardContent>
      </Card>

      {/* Entry navigator */}
      {(state.current_view === 'entry') && (
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ImageIcon className="h-4 w-4" />Huidige inzending op beamer</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <EntryNavigator entries={approved} currentId={state.current_entry_id}
                            onPick={id => patch({ current_entry_id: id })} disabled={saving} />
          </CardContent>
        </Card>
      )}

      {/* Admin Reveal */}
      {state.current_view === 'reveal' && (
        <RevealControls kind="admin" state={state} total={revealTotal} reveal={data.current_reveal}
                        revealOrder={data.reveal_order || []} entries={approved}
                        patch={patch} disabled={saving} />
      )}
      {/* Publiek Reveal */}
      {state.current_view === 'public_reveal' && publicEnabled && (
        <RevealControls kind="public" state={state} total={publicRevealTotal} reveal={data.current_public_reveal}
                        revealOrder={data.public_reveal_order || []} entries={approved}
                        patch={patch} disabled={saving} />
      )}

      {/* Standings snapshot */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Tussenstand ({data.standings?.length || 0})</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          {(data.standings || []).map((r, i) => (
            <div key={r.entry_id} className="flex items-center gap-3 py-2">
              <div className="w-6 text-center text-muted-foreground tabular-nums">{r.revealed ? i + 1 : '—'}</div>
              <div className="w-12 h-12 rounded border border-border overflow-hidden flex items-center justify-center bg-muted/30 shrink-0">
                {r.image_url ? <img src={r.image_url} alt="" className="max-w-full max-h-full object-contain" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.title}</div><div className="text-xs text-muted-foreground">{r.team_name}</div></div>
              <div className="text-xl font-bold tabular-nums w-16 text-right">{r.points ?? '—'}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function EntryNavigator({ entries, currentId, onPick, disabled }) {
  const idx = entries.findIndex(e => e.id === currentId)
  const prev = idx > 0 ? entries[idx - 1] : null
  const next = idx >= 0 && idx < entries.length - 1 ? entries[idx + 1] : (idx === -1 ? entries[0] : null)
  const cur = idx >= 0 ? entries[idx] : null
  return (
    <>
      <div className="flex items-center gap-3">
        <Button variant="outline" disabled={disabled || !prev} onClick={() => prev && onPick(prev.id)}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex-1 text-center">
          {cur ? <><div className="font-bold">{cur.title}</div><div className="text-xs text-muted-foreground">{cur.team_name}</div></>
              : <div className="text-muted-foreground">Nog niks geselecteerd</div>}
        </div>
        <Button disabled={disabled || !next} onClick={() => next && onPick(next.id)}>
          {next ? next.team_name : '—'} <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {entries.map(e => (
          <Button key={e.id} size="sm" variant={e.id === currentId ? 'default' : 'outline'} disabled={disabled}
                  onClick={() => onPick(e.id)}>
            #{e.running_order} · {e.team_code || e.team_name.slice(0,4)}
          </Button>
        ))}
      </div>
    </>
  )
}

function RevealControls({ kind, state, total, reveal, revealOrder, entries, patch, disabled }) {
  const isPub = kind === 'public'
  const stepKey = isPub ? 'public_reveal_step' : 'reveal_step'
  const step = state[stepKey] || 0
  const nextId = revealOrder[step]
  const nextEntry = nextId ? entries.find(e => e.id === nextId) : null
  const advance = () => patch({ [stepKey]: Math.min(step + 1, total) })
  const back    = () => patch({ [stepKey]: Math.max(step - 1, 0) })
  const reset   = () => patch({ [stepKey]: 0 })
  const accent  = isPub ? 'cyan' : 'purple'
  const border  = isPub ? 'border-cyan-500/40 bg-cyan-500/5' : 'border-purple-500/40 bg-purple-500/5'
  const iconCol = isPub ? 'text-cyan-400' : 'text-purple-400'
  const btnBg   = isPub ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-purple-500 hover:bg-purple-600'
  const label   = isPub ? 'Publiek reveal' : 'Admin reveal'
  return (
    <Card className={border}>
      <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Trophy className={`h-4 w-4 ${iconCol}`} />{label}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Getoond</div>
            <div className={`text-2xl font-bold tabular-nums ${iconCol}`}>{reveal ? (isPub ? reveal.points_public : reveal.points_admin) : '—'}</div>
            <div className="text-xs text-muted-foreground truncate">{reveal?.team_name || 'nog niks'}</div>
            {isPub && reveal && <div className="text-xs text-muted-foreground">totaal: {reveal.points}</div>}
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums">{step}/{total}</div>
            <div className="text-xs text-muted-foreground uppercase">Onthuld</div>
          </div>
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Volgende</div>
            <div className="text-2xl font-bold text-pink-500 tabular-nums">?</div>
            <div className="text-xs text-muted-foreground truncate">{nextEntry ? nextEntry.team_name : (step >= total ? 'klaar' : '—')}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="lg" onClick={reset} disabled={disabled || step === 0}>
            <RotateCcw className="h-5 w-5 mr-1" />Reset
          </Button>
          <Button variant="outline" size="lg" onClick={back} disabled={disabled || step === 0}><ChevronLeft className="h-5 w-5" /></Button>
          <Button size="lg" className={`flex-1 h-14 text-white ${btnBg}`}
                  onClick={advance} disabled={disabled || step >= total}>
            <ChevronRight className="h-6 w-6 mr-2" />
            {step >= total ? 'Alles onthuld' : `Reveal → ${nextEntry?.team_name || ''}`}
          </Button>
        </div>
        <div className="text-xs text-muted-foreground text-center">
          {isPub ? 'Volgorde = laagste publiek-punten eerst · bovenop admin totalen' : 'Volgorde = laagste admin-punten eerst'}
        </div>
      </CardContent>
    </Card>
  )
}
